import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  handleAdminOperationsRestart,
  handleAdminOperationsStatus,
} from "../../services/APIClient";
import Toster from "../../utils/Toaster";

const ALERT_COOLDOWN_MS = 15 * 60 * 1000;
const ALERT_STORAGE_KEY = "ninetysixOperationsLastAlert";

const statusStyles = {
  operational: "border-emerald-400/25 bg-emerald-400/10 text-emerald-200",
  degraded: "border-amber-400/25 bg-amber-400/10 text-amber-200",
  down: "border-red-400/25 bg-red-400/10 text-red-200",
  checking: "border-white/15 bg-white/[0.06] text-white/60",
};

const formatUptime = (seconds = 0) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const StatusPill = ({ status }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusStyles[status] || statusStyles.checking}`}>
    {status || "checking"}
  </span>
);

const HealthCard = ({ label, status, detail, meta }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.045] p-5">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">{label}</p>
        <p className="mt-3 text-sm leading-6 text-white/65">{detail}</p>
      </div>
      <StatusPill status={status} />
    </div>
    {meta && <p className="mt-4 rounded-xl bg-black/25 px-3 py-2 text-xs text-white/45">{meta}</p>}
  </div>
);

const Operations = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restartBusy, setRestartBusy] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(() =>
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported"
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleAdminOperationsStatus();
      setStatus(response?.data || response?.response || response || null);
    } catch (error) {
      setStatus(null);
      Toster("Operations status could not be refreshed.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60000);
    return () => clearInterval(interval);
  }, [refresh]);

  const restartEnabled = status?.restart?.enabled === true;

  const overallStatus = useMemo(() => {
    const values = [
      status?.status,
      ...(status?.publicChecks || []).map((check) => check.status),
    ].filter(Boolean);
    if (values.some((value) => value === "down")) return "down";
    if (values.some((value) => value === "degraded")) return "degraded";
    return values.length ? "operational" : "checking";
  }, [status]);

  const issues = useMemo(() => {
    const currentIssues = [];
    if (status?.database && !status.database.ready) {
      currentIssues.push(`MongoDB is ${status.database.status || "not ready"}`);
    }
    (status?.publicChecks || []).forEach((check) => {
      if (check.status === "down" || check.status === "degraded") {
        currentIssues.push(`${check.label} is ${check.status}${check.httpStatus ? ` (HTTP ${check.httpStatus})` : ""}`);
      }
    });
    return currentIssues;
  }, [status]);

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      setNotificationPermission("unsupported");
      Toster("This browser does not support desktop notifications.", "error");
      return;
    }
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    if (permission === "granted") {
      Toster("Operations notifications are enabled.", "success");
    }
  };

  useEffect(() => {
    if (!issues.length || notificationPermission !== "granted") return;

    const now = Date.now();
    const lastAlert = Number(localStorage.getItem(ALERT_STORAGE_KEY) || 0);
    if (now - lastAlert < ALERT_COOLDOWN_MS) return;

    const body = issues.slice(0, 3).join("\n");
    new Notification("NinetySix needs attention", {
      body,
      tag: "ninetysix-operations-alert",
      requireInteraction: false,
    });
    localStorage.setItem(ALERT_STORAGE_KEY, String(now));
  }, [issues, notificationPermission]);

  const requestRestart = async () => {
    if (!restartEnabled) {
      Toster("Restart is locked until ENABLE_ADMIN_RESTART is enabled on the server.", "error");
      return;
    }
    const confirmed = window.confirm("Restart the NinetySix API now? Active users may need to retry for a moment.");
    if (!confirmed) return;
    setRestartBusy(true);
    try {
      await handleAdminOperationsRestart();
      Toster("Restart requested. Check status again in a few moments.", "success");
    } catch (error) {
      Toster("Restart request failed or is locked.", "error");
    } finally {
      setRestartBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090D] text-white">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-7 sm:px-6 lg:px-8 lg:py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#67A3FA]">System health</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">Operations & Alerts</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-white/50 sm:text-base">
              See whether the website, API, MongoDB, and ChatGPT connector are running well.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill status={overallStatus} />
            <button
              type="button"
              onClick={requestNotificationPermission}
              className="rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white/80 transition hover:border-[#67A3FA]/40 hover:bg-white/[0.1]"
            >
              {notificationPermission === "granted" ? "Notifications on" : "Enable alerts"}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="rounded-full border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-semibold text-white/80 transition hover:border-[#67A3FA]/40 hover:bg-white/[0.1] disabled:opacity-50"
            >
              {loading ? "Checking..." : "Refresh"}
            </button>
          </div>
        </header>

        {issues.length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-300/25 bg-amber-400/10 px-5 py-4 text-sm text-amber-100">
            <p className="font-semibold">Needs attention</p>
            <p className="mt-1 text-amber-100/75">{issues.slice(0, 3).join(" • ")}</p>
          </div>
        )}

        <div className="mb-7 grid gap-4 lg:grid-cols-3">
          <HealthCard
            label="Backend API"
            status={status?.status || "checking"}
            detail={status ? `Environment: ${status.environment || "unknown"}` : "Waiting for backend status."}
            meta={status ? `Uptime ${formatUptime(status.uptimeSeconds)} - checked ${new Date(status.checkedAt).toLocaleString()}` : null}
          />
          <HealthCard
            label="MongoDB"
            status={status?.database?.ready ? "operational" : status ? "degraded" : "checking"}
            detail={status?.database?.ready ? "Database connection is ready." : "Database readiness is not confirmed."}
            meta={status?.database ? `${status.database.host || "host hidden"} - ${status.database.name || "database hidden"}` : null}
          />
          <HealthCard
            label="Server memory"
            status="operational"
            detail={status?.memory ? `${status.memory.heapUsedMb} MB heap used of ${status.memory.heapTotalMb} MB.` : "Waiting for memory status."}
            meta={status?.memory ? `${status.memory.rssMb} MB resident memory` : null}
          />
        </div>

        <section className="mb-7 rounded-3xl border border-white/10 bg-[#0D1017] p-5 sm:p-7">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#67A3FA]">Public checks</p>
              <h2 className="mt-2 text-xl font-semibold text-white sm:text-2xl">What users can reach</h2>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {(status?.publicChecks || []).map((check) => (
              <HealthCard
                key={check.key}
                label={check.label}
                status={check.status || "checking"}
                detail={check.httpStatus ? `HTTP ${check.httpStatus}` : (check.error || "Checking reachability.")}
                meta={check.latencyMs != null ? `${check.latencyMs} ms - ${check.url}` : check.url}
              />
            ))}
            {!status?.publicChecks?.length && (
              <HealthCard
                label="Public services"
                status="checking"
                detail="Waiting for backend reachability checks."
                meta="Website, admin panel, and ChatGPT MCP"
              />
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-[#0D1017] p-5 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#67A3FA]">Recovery control</p>
              <h2 className="mt-2 text-xl font-semibold text-white sm:text-2xl">API restart</h2>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-white/50">
                Restart is locked by default. Enable it only when the server is managed by PM2 or systemd so the API comes back automatically.
              </p>
              {status?.restart?.note && <p className="mt-3 text-xs text-white/40">{status.restart.note}</p>}
            </div>
            <button
              type="button"
              onClick={requestRestart}
              disabled={restartBusy || !restartEnabled}
              className="rounded-full border border-red-300/20 bg-red-400/10 px-5 py-3 text-sm font-semibold text-red-100 transition hover:bg-red-400/15 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {restartBusy ? "Requesting..." : restartEnabled ? "Restart API" : "Restart locked"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Operations;
